const splitName = (name: string): string[] => name.split(/\s+/).filter(Boolean)

export const getFirstName = (name: string): string => splitName(name)[0] ?? ''

/** "Michael Prince" → "Michael P." — enough to tell two Michaels apart without the full surname. */
export const getShortName = (name: string): string => {
  const words = splitName(name)
  if (words.length < 2) return words[0] ?? ''
  // Spread, not [0]: a surname starting with a non-BMP character would otherwise yield a lone surrogate.
  return `${words[0]} ${[...words[words.length - 1]][0].toUpperCase()}.`
}

// Hues picked to keep white initials readable; deliberately tenant-independent so a
// participant keeps one colour everywhere (strip + attribution rows) on any theme.
export const AVATAR_PALETTE = [
  '#c62828',
  '#1565c0',
  '#00695c',
  '#b34700',
  '#4527a0',
  '#33691e',
  '#ad1457',
  '#6a1b9a',
] as const

export const getAvatarColor = (key: string): string => {
  let hash = 5381
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 33 + key.charCodeAt(index)) >>> 0
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}

export interface CateringTeamOrderParticipant {
  userId: string
  name: string
}

/**
 * Owner first, then contributors in backend order, deduped by userId (the backend derives
 * contributors from item attribution, so the owner appears there too once they add items).
 * Shared by the strip and the palette so colour order matches display order exactly.
 */
export const buildParticipantList = (
  ownerUserId: number | string | null,
  ownerName: string,
  contributors: { userId: number | string; name: string }[],
): CateringTeamOrderParticipant[] => {
  const participants: CateringTeamOrderParticipant[] = []
  const seen = new Set<string>()

  if (ownerUserId != null) {
    participants.push({ userId: String(ownerUserId), name: ownerName })
    seen.add(String(ownerUserId))
  }

  contributors.forEach((contributor) => {
    const userId = String(contributor.userId)
    if (seen.has(userId)) return
    seen.add(userId)
    participants.push({ userId, name: contributor.name })
  })

  return participants
}

/**
 * Placed orders carry no contributor roster, so derive one from the owner plus the attributed
 * lines — same owner-first rule as the basket, so each person keeps their colour after checkout.
 */
export const buildParticipantsFromLines = (
  owner: { id: number | string; fullName: string } | null | undefined,
  lines: ReadonlyArray<{ addedBy?: { id: number | string; fullName: string } | null }>,
): CateringTeamOrderParticipant[] =>
  buildParticipantList(
    owner?.id ?? null,
    owner?.fullName ?? '',
    lines.flatMap((line) =>
      line.addedBy ? [{ userId: line.addedBy.id, name: line.addedBy.fullName }] : [],
    ),
  )
